import React, { createContext, useState, useContext, useEffect } from "react";
import { toast } from 'sonner';
import { useNavigate } from "react-router-dom";

// Create context
const GnosisContext = createContext();

// Custom hook to use the Gnosis context
export const useGnosis = () => useContext(GnosisContext);

// Initial state for a timer
const initialTimerState = {
  recipient: "",
  duration: "1",     // Target duration in minutes
  elapsedTime: 0,    // Elapsed time in seconds (counting up)
  isActive: false,
  targetReached: false,
  lastUpdated: Date.now(),
  notified: false    // Flag to track if notification was shown
};

export const GnosisProvider = ({ children }) => {
  const [timers, setTimers] = useState({
    1: { ...initialTimerState, id: 1, title: "Timer 1", color: "yellow" },
    2: { ...initialTimerState, id: 2, title: "Timer 2", color: "yellow" },
    3: { ...initialTimerState, id: 3, title: "Timer 3", color: "yellow" },
    4: { ...initialTimerState, id: 4, title: "Timer 4", color: "yellow" }
  });
  const navigate = useNavigate();

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Update recipient
  const updateRecipient = (timerId, recipient) => {
    setTimers(prev => ({
      ...prev,
      [timerId]: {
        ...prev[timerId],
        recipient,
        // Update title to reflect recipient name if one is provided
        title: recipient ? `Timer for ${recipient}` : `Timer ${timerId}`
      }
    }));
  };

  // Update duration
  const updateDuration = (timerId, duration) => {
    setTimers(prev => ({
      ...prev,
      [timerId]: {
        ...prev[timerId],
        duration
      }
    }));
  };

  // Toggle timer (start/pause)
  const toggleTimer = (timerId) => {
    setTimers(prev => {
      const timer = prev[timerId];

      // Only show error toast if trying to start timer without recipient
      // and don't show it repeatedly
      if (!timer.recipient.trim() && !timer.isActive) {
        // Use a separate setTimeout to avoid React state update conflicts
        setTimeout(() => {
          // Using an ID for the toast helps prevent duplicates
          toast.error("Please enter a recipient name", {
            id: `missing-recipient-${timerId}`,
            duration: 3000
          });
        }, 0);
        return prev;
      }

      return {
        ...prev,
        [timerId]: {
          ...timer,
          isActive: !timer.isActive,
          lastUpdated: Date.now()
        }
      };
    });
  };

  // Reset timer
  const resetTimer = (timerId) => {
    setTimers(prev => ({
      ...prev,
      [timerId]: {
        ...prev[timerId],
        isActive: false,
        elapsedTime: 0,
        targetReached: false,
        notified: false,
        lastUpdated: Date.now()
      }
    }));
  };

  // Show notification for timer target reached
  const showTimerNotification = (timerId, recipient) => {
    // Show toast with the captured recipient name
    const toastId = `timer-complete-${timerId}-${Date.now()}`;
    
    toast(
      <div className="flex flex-col">
        <span className="font-bold text-lg">Time is up for Recipient:</span>
        <span className="text-xl mt-1">{recipient}</span>
      </div>,
      {
        id: toastId,
        duration: Infinity, // Keep notification until manually closed
        action: {
          label: "Go to Gnosis",
          onClick: () => navigate("/gnosis")
        },
        style: {
          background: "#FFC107",
          color: "#000000",
          border: "1px solid #FFC107"
        },
        onDismiss: () => {
          // When notification is dismissed, mark the timer as notified
          setTimers(prev => ({
            ...prev,
            [timerId]: {
              ...prev[timerId],
              notified: true
            }
          }));
        }
      }
    );
  };

  // Tick all active timers
  useEffect(() => {
    const tickInterval = setInterval(() => {
      const now = Date.now();
      
      setTimers(prev => {
        let updated = false;
        const newTimers = { ...prev };
        
        // Check each timer
        Object.keys(prev).forEach(id => {
          const timer = prev[id];
          
          if (timer.isActive) {
            // Calculate elapsed time since last update (in seconds)
            const elapsed = Math.floor((now - timer.lastUpdated) / 1000);
            
            if (elapsed >= 1) {
              // Update elapsed time
              const newElapsedTime = timer.elapsedTime + elapsed;
              
              // Calculate target time in seconds
              const targetTime = parseInt(timer.duration) * 60;
              
              // Check if target just reached and notification not shown yet
              const justReachedTarget = !timer.targetReached && 
                                       timer.elapsedTime < targetTime && 
                                       newElapsedTime >= targetTime;
              
              newTimers[id] = {
                ...timer,
                elapsedTime: newElapsedTime,
                targetReached: timer.targetReached || newElapsedTime >= targetTime,
                lastUpdated: now
              };
              
              updated = true;
              
              // If target just reached and notification not yet shown
              if (justReachedTarget && !timer.notified) {
                newTimers[id].targetReached = true;
                
                // Use setTimeout to push notification to next event loop cycle
                setTimeout(() => {
                  showTimerNotification(id, timer.recipient);
                }, 100);
              }
            }
          }
        });
        
        return updated ? newTimers : prev;
      });
    }, 500); // Check every half second for more accurate timing
    
    return () => clearInterval(tickInterval);
  }, []);

  // Get timer status text
  const getTimerStatus = (timer) => {
    if (!timer.isActive && timer.elapsedTime === 0) {
      return "Ready";
    } else if (!timer.isActive) {
      return "Paused";
    } else if (timer.targetReached) {
      return "Overtime";
    } else {
      return "Running";
    }
  };

  // Create context value
  const value = {
    timers,
    formatTime,
    updateRecipient,
    updateDuration,
    toggleTimer,
    resetTimer,
    getTimerStatus
  };

  return (
    <GnosisContext.Provider value={value}>
      {children}
    </GnosisContext.Provider>
  );
};

export default GnosisContext;